import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DownloadSimple, Trash } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { api } from "../../lib/api";

const titles: Record<string, string> = {
  contact: "Contact Messages",
  quotes: "Quote Requests",
  applications: "Job Applications",
};

const statusOptions = ["new", "in_review", "responded", "archived"];

export const SubmissionsPanel = ({ kind }: { kind: "contact" | "quotes" | "applications" }) => {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery<any[]>({
    queryKey: ["submissions", kind],
    queryFn: async () => (await api.get(`/admin/submissions/${kind}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["submissions", kind] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const setStatus = async (id: string, status: string) => {
    await api.put(`/admin/submissions/${kind}/${id}/status`, { status });
    refresh();
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    await api.delete(`/admin/submissions/${kind}/${id}`);
    toast.success("Deleted.");
    refresh();
  };

  const downloadResume = async (item: any) => {
    try {
      const res = await api.get(`/admin/resume/${item.resume_file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.resume_filename || "resume.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download resume.");
    }
  };

  return (
    <div data-testid={`submissions-panel-${kind}`}>
      <p className="overline-label mb-2">Inbox</p>
      <h1 className="font-heading font-medium text-3xl tracking-tight text-ink mb-8">{titles[kind]}</h1>

      {isLoading && <p className="text-sm text-sub">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-sub border border-line p-8 text-center">No submissions yet.</p>
      )}

      <div className="space-y-4">
        {items.map((item, i) => (
          <article key={item.id} data-testid={`submission-item-${i}`} className="border border-line bg-panel p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-heading font-medium text-lg tracking-tight text-ink">{item.name}</h3>
                  <Badge variant="outline" className="rounded-none font-mono-tech text-[10px] uppercase tracking-wider">
                    {item.status}
                  </Badge>
                </div>
                <p className="mt-1 text-xs font-mono-tech text-sub break-all">
                  {item.email}
                  {item.phone ? ` · ${item.phone}` : ""} · {new Date(item.created_at).toLocaleString()}
                </p>
                {kind === "contact" && (
                  <>
                    {item.subject && <p className="mt-3 text-sm font-medium text-ink">{item.subject}</p>}
                    <p className="mt-2 text-sm text-sub leading-relaxed whitespace-pre-wrap">{item.message}</p>
                  </>
                )}
                {kind === "quotes" && (
                  <>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[item.service_interest, item.project_type, item.budget, item.timeline, item.company]
                        .filter(Boolean)
                        .map((v: string) => (
                          <span key={v} className="font-mono-tech text-[10px] tracking-wider uppercase px-2 py-1 border border-line text-sub">
                            {v}
                          </span>
                        ))}
                    </div>
                    <p className="mt-3 text-sm text-sub leading-relaxed whitespace-pre-wrap">{item.details}</p>
                  </>
                )}
                {kind === "applications" && (
                  <>
                    <p className="mt-3 text-sm font-medium text-ink">Position: {item.career_title}</p>
                    {item.cover_letter && (
                      <p className="mt-2 text-sm text-sub leading-relaxed whitespace-pre-wrap">{item.cover_letter}</p>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {kind === "applications" && item.resume_file_id && (
                  <button
                    data-testid={`resume-download-btn-${i}`}
                    onClick={() => downloadResume(item)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-line text-blueprint hover:border-[var(--blueprint)] transition-colors"
                  >
                    <DownloadSimple size={14} /> Resume
                  </button>
                )}
                <Select value={item.status} onValueChange={(v) => setStatus(item.id, v)}>
                  <SelectTrigger className="w-32 h-9 rounded-none text-xs" data-testid={`status-select-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  data-testid={`submission-delete-btn-${i}`}
                  onClick={() => remove(item.id)}
                  className="p-2 border border-line text-sub hover:text-red-500 hover:border-red-400 transition-colors"
                  aria-label="Delete"
                >
                  <Trash size={15} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
